import { createContext, useContext, useCallback, useRef, type ReactNode } from 'react';
import { useWebSocket, type WSEvent } from '~/hooks/useWebSocket';

type EventListener = (event: WSEvent) => void;

interface WebSocketContextValue {
  connected: boolean;
  reconnecting: boolean;
  error: string | null;
  subscribe: (listener: EventListener) => () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

/**
 * Provides a single shared WebSocket connection for all consumers.
 * Subscribes to both 'signals' and 'messages' channels.
 */
export function WebSocketProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const listenersRef = useRef<Set<EventListener>>(new Set());

  const handleEvent = useCallback((event: WSEvent) => {
    for (const listener of listenersRef.current) {
      listener(event);
    }
  }, []);

  const ws = useWebSocket({
    channels: ['signals', 'messages'],
    onEvent: handleEvent,
    enabled,
  });

  const subscribe = useCallback((listener: EventListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{
      connected: ws.connected,
      reconnecting: ws.reconnecting,
      error: ws.error,
      subscribe,
    }}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Access the shared WebSocket connection state and event subscription.
 */
export function useWebSocketContext() {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return ctx;
}
